//* LessonPlayer (in content) wrapper, specifically for the 'learner' mode
//* storing the Portfolio and Answers.

import React, { useEffect, useRef, useState } from 'react';

import AlertDialog from '../../shared/components/AlertDialog';
import { ApiPortfolioState } from '../models/ApiPortfolioState';
import LessonLaunchParamsV2 from '../models/LessonLaunchParamsV2';
import { LessonPlayerWithAnswers } from '../../shared/components/LessonPlayerWithAnswers';
import { LessonSubmissionAction } from '../../content/components/LessonSubmissionPanel';
import { SUPPORT_MESSAGE } from '../../shared/constants';
import { useLessonResults } from '../hooks/useLessonResults';

type LessonPlayerAction =
  | 'load_lesson'
  | 'save_answer'
  | 'finalize_lesson'
  | 'submit_lesson'
  | 'unsubmit_lesson'
  | 'delete_answer';

export interface LessonPlayerError {
  title: string;
  message: string;
}
export const LessonPlayerErrors: {
  [action in LessonPlayerAction]: LessonPlayerError;
} = {
  load_lesson: {
    title: 'Failed to load your lesson',
    message: SUPPORT_MESSAGE
  },
  save_answer: {
    title: 'Failed to save your answer',
    message: SUPPORT_MESSAGE
  },
  finalize_lesson: {
    title: 'Failed to finalise your lesson',
    message: SUPPORT_MESSAGE
  },
  submit_lesson: {
    title: 'Failed to submit your lesson',
    message: SUPPORT_MESSAGE
  },
  unsubmit_lesson: {
    title: '',
    message:
      'Your submitted assessment cannot be withdrawn as a marker has already been assigned and marking has begun.'
  },
  delete_answer: {
    title: 'Failed to delete your answer',
    message: SUPPORT_MESSAGE
  }
};

interface LessonPlayerNativeProps {
  open: boolean;
  preview: boolean;
  coursekey?: number;
  launchParamsV2: LessonLaunchParamsV2;
  onCourseUpdate: () => void;
  onClose: () => void;
  onContinueNextLesson?: (lessonCompleted: boolean) => void;
}

export function LessonPlayerNative(props: LessonPlayerNativeProps) {
  const {
    title,
    lessonkey,
    lessonVersion,
    lessonType,
    displaySettings,
    state,
    status,
    completedDate,
    layer,
    completionType,
    completionThreshold,
    portfolioLocked,
    hasCert,
    required,
    lastLesson,
    timeToComplete
  } = props.launchParamsV2;
  const [lessonPlayerError, setLessonPlayerError] =
    useState<LessonPlayerError>(null);
  const [lessonState, setLessonState] = useState<ApiPortfolioState>(state);
  const quizLockedRef = useRef(state === ApiPortfolioState.QuizLock);

  const {
    finishLessonSession,
    finishingSession,
    finishSessionError,
    finishedAttempt,
    submitLesson,
    submittingLesson,
    submitLessonError,
    submittedLesson,
    unsubmitLesson,
    unsubmittingLesson,
    unsubmitLessonError,
    unsubmittedLesson
  } = useLessonResults(props.coursekey);

  useEffect(() => {
    if (submittedLesson) {
      setLessonState(submittedLesson.state);
      props.onCourseUpdate();
    }
  }, [submittedLesson]);

  useEffect(() => {
    if (unsubmittedLesson) {
      setLessonState(unsubmittedLesson.state);
      props.onCourseUpdate();
    }
  }, [unsubmittingLesson]);

  useEffect(() => {
    if (finishSessionError) {
      setLessonPlayerError(LessonPlayerErrors.finalize_lesson);
      return;
    }
    if (submitLessonError) {
      setLessonPlayerError(LessonPlayerErrors.submit_lesson);
      return;
    }
    if (unsubmitLessonError) {
      setLessonPlayerError(LessonPlayerErrors.unsubmit_lesson);
      return;
    }
  }, [finishSessionError, submitLessonError, unsubmitLessonError]);

  function handleComplete(completed: boolean) {
    //* The 'NEXT / FINALISE' button was clicked.
    if (lessonType === 'quiz') {
      quizLockedRef.current = true;
    }
    finishLessonSession(lessonType, lessonkey, lessonVersion, layer, completed);
  }

  function handleSubmitLesson(action: LessonSubmissionAction) {
    if (props.preview) {
      action === 'submit'
        ? setLessonState(ApiPortfolioState.AssessmentSubmit)
        : setLessonState(ApiPortfolioState.AssessmentStart);
    } else {
      if (action === 'withdraw') {
        unsubmitLesson(lessonkey, lessonVersion, layer);
      } else {
        submitLesson(
          lessonkey,
          lessonVersion,
          layer,
          state === ApiPortfolioState.AssessmentRetry
        );
      }
    }
  }

  return (
    <>
      <LessonPlayerWithAnswers
        lessonkey={lessonkey}
        lessonVersion={lessonVersion}
        layer={layer}
        quizLocked={quizLockedRef.current}
        open={props.open}
        displayMode={props.preview ? 'preview' : 'learn'}
        state={finishedAttempt?.state || lessonState}
        complete={!!finishedAttempt?.completed || !!completedDate}
        status={finishedAttempt?.status || status}
        hasCert={hasCert}
        lessonTitle={title}
        lessonType={lessonType}
        displaySettings={displaySettings}
        completionType={completionType}
        completionThreshold={completionThreshold}
        portfolioLocked={portfolioLocked}
        inProgress={finishingSession || submittingLesson || unsubmittingLesson}
        required={required}
        lastLesson={lastLesson}
        timeToComplete={timeToComplete}
        onComplete={handleComplete}
        onSubmitLesson={handleSubmitLesson}
        onClose={props.onClose}
        onContinueNextLesson={props.onContinueNextLesson}
      />
      {lessonPlayerError && (
        <AlertDialog
          title={lessonPlayerError.title}
          message={lessonPlayerError.message}
          onClose={() => setLessonPlayerError(null)}
        />
      )}
    </>
  );
}
